import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { MapPin, ChevronRight } from 'lucide-react'
import { DotGlobe } from './DotGlobe'
import { useGlobeHero, PointSheet, greetingFor } from './globe-chrome'
import { ConditionsHero } from '@/features/home/ConditionsHero'
import { useDayFactor } from '@/features/home/useDayFactor'
import { useInView } from '@/components/hooks/useInView'
import type { NwsObservation, NwsForecast, NwsPeriod, NwsPoint } from '@/lib/nws/types'
import './globe.css'

interface HomeGlobeProps {
  location: { lat: number; lon: number; label: string }
  observation: NwsObservation | null
  forecast: NwsForecast | undefined
  currentPeriod: NwsPeriod | undefined
  point: NwsPoint | undefined
  hi: number | null
  lo: number | null
  /** Alert bar — rendered in the panel foot (with the chips), so it sits full-width
      below the globe on mobile and inside the right content column on desktop. */
  alert?: ReactNode
}

// The home globe is large, axially TILTED, and bleeds past the screen edges — the
// page is composed AROUND it, not a boxed widget. North America + the live points
// sit in the clear middle band; greeting sits above, conditions + chips below.
const HOME_TILT = 0.3
const HOME_ROLL = -0.42

export function HomeGlobe({ location, observation, forecast, currentPeriod, point, hi, lo, alert }: HomeGlobeProps) {
  const nav = useNavigate()
  const { points, now, selected, setSelected } = useGlobeHero(location)
  const greeting = greetingFor(now)
  const activeCount = points.length
  // Pause the animated condition icons' CSS loops when the hero scrolls off-screen
  // (the globe canvas already self-pauses via its own IntersectionObserver).
  const condView = useInView<HTMLDivElement>()

  // Day brighten for the dots only (the full-page colour lives in SkyTint). Same factor +
  // ?tint override; never below 1 so nothing ever dims the globe.
  const factor = useDayFactor(location.lat, location.lon)

  return (
    <section className="home-hero" aria-label="Current conditions and live globe">
      {/* Globe is a HERO-ZONE element: the clip masks its dots to nothing above the chip
          grid and carves a soft pocket around the temperature text (see globe.css). */}
      <div className="home-hero__globe-clip" aria-hidden="true">
        <div
          className="home-hero__globe"
          style={{ '--dot-bright': 1 + factor * 0.18 } as React.CSSProperties}
        >
          <DotGlobe
            points={points}
            userLat={location.lat}
            userLon={location.lon}
            tilt={HOME_TILT}
            roll={HOME_ROLL}
            onSelect={setSelected}
            ariaLabel={`Rotating globe showing ${activeCount} active weather point${
              activeCount === 1 ? '' : 's'
            } worldwide`}
          />
        </div>
      </div>

      {/* greeting (top-right) → conditions readout (mid, over the globe's curve) → chips
          (full-width below / right column on desktop). The pause-on-scroll ref lives on
          the panel so the chip + glyph animations idle when the hero is off-screen. */}
      <div
        ref={condView.ref}
        className={`home-hero__panel${condView.inView ? '' : ' is-paused'}`}
      >
        <div className="home-hero__greet">
          <span className="home-hero__hello">{greeting}</span>
          <Link to="/locations" className="home-hero__loc">
            <MapPin size={14} />
            <span>{location.label}</span>
            <ChevronRight size={13} />
          </Link>
        </div>

        <ConditionsHero
          observation={observation}
          forecast={forecast}
          currentPeriod={currentPeriod}
          hi={hi}
          lo={lo}
          lat={location.lat}
          lon={location.lon}
          point={point}
          alert={alert}
        />
      </div>

      {selected && (
        <PointSheet
          point={selected}
          onClose={() => setSelected(null)}
          onOpen={() => {
            const href = selected.href
            setSelected(null)
            nav(href)
          }}
        />
      )}
    </section>
  )
}
